import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles, type Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article: Article | undefined = articles.find((a) => a.slug === slug);

  const relatedArticles = article
    ? articles.filter((a) => article.relatedSlugs.includes(a.slug))
    : [];

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 开云官网`;
    } else {
      document.title = '文章未找到 - 开云官网';
    }
  }, [article]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h1 className="text-2xl font-bold font-heading text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link to="/" className="px-5 py-2.5 rounded-md bg-primary-500 text-white text-sm hover:bg-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            author: { '@type': 'Person', name: article.author },
            image: article.image,
            publisher: {
              '@type': 'Organization',
              name: '开云平台',
            },
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '新闻资讯', href: '/category/news' },
                { label: article.title },
              ]}
            />

            <article className="max-w-3xl mx-auto">
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                  <span className="text-xs text-foreground-400">作者：{article.author}</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <p className="text-sm text-foreground-500 leading-relaxed">{article.excerpt}</p>
              </div>

              <div className="mb-10 rounded-lg overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-auto object-cover"
                />
              </div>

              <div
                className="prose prose-sm max-w-none text-foreground-700 leading-relaxed
                  [&_h2]:text-xl [&_h2]:font-bold [&_h2]:font-heading [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-4
                  [&_p]:mb-4 [&_p]:text-sm [&_p]:leading-relaxed
                  [&_strong]:text-foreground-900 [&_strong]:font-semibold"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-6 border-t border-background-200/60">
                <div className="flex items-center gap-2 flex-wrap mb-6">
                  <span className="text-xs text-foreground-400">标签：</span>
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[11px]">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {relatedArticles.length > 0 && (
                <div className="mt-10 pt-6 border-t border-background-200/60">
                  <h3 className="text-base font-semibold font-heading text-foreground-900 mb-5">相关文章</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/article/${ra.slug}`}
                        className="flex gap-3 p-3 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors group"
                      >
                        <div className="w-20 h-16 rounded-md overflow-hidden flex-shrink-0">
                          <img src={ra.image} alt={ra.title} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-xs font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors">
                            {ra.title}
                          </h4>
                          <span className="text-[10px] text-foreground-400 mt-1 block">{ra.date}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </article>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}